import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import {
  submitContactInquiry,
  uploadInquiryAttachment,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  ACCEPTED_ATTACHMENTS,
} from '../services/inquiryService';
import { umamiTrack } from '../services/analytics';
import { SITE_ORIGIN } from '../config/siteOrigin';
import { formatBytes } from '../utils/format';

// ── „Kontakt" ─────────────────────────────────────────────────────────────────────────────
// Ein Formular statt mehrerer Mailadressen: die Anfragen laufen in den Posteingang unter
// /admin/anfragen, wo sie einen Bearbeitungsstand bekommen. Bewusst *keine* info@/redaktion@/
// technik@ auf der Seite — sonst muesste das Team doch wieder Postfaecher pruefen.
// Zweiter Teil der Seite sind die Bedingungen fuer Pressemitteilungen.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

const PAGE_TITLE = 'Kontakt – Fragen, Anregungen & Presse | Radsport Events';
const PAGE_DESC =
  'Fragen, Anmerkungen, Kritik oder Vorschläge zu radsport-events.de? Schreib uns über das Kontaktformular — zu Redaktion, Technik oder Presse, auf Wunsch mit Screenshot oder Foto.';
const CANONICAL = `${SITE_ORIGIN}/kontakt`;

/**
 * Themen der Anfrage. Ersetzen die drei Postfaecher aus Romans Entwurf: dieselbe Zuordnung,
 * aber sie steuert die Filterung im Posteingang statt einer Mailadresse.
 */
const TOPICS = [
  { value: 'Allgemeine Anfrage', hint: 'Alles, was sonst nirgends passt' },
  { value: 'Redaktion & Inhalte', hint: 'Veranstaltungen, News, Einträge, Inhalte' },
  { value: 'Technik & Fehler', hint: 'Probleme, Fehlermeldungen, Fragen zur Website' },
  { value: 'Presse & Medien', hint: 'Pressemitteilungen, Logos, Berichterstattung' },
];

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Berry-Sternchen zur Kennzeichnung von Pflichtfeldern. */
const RequiredMark: React.FC = () => (
  <span aria-hidden style={{ color: '#922C57', marginLeft: 3 }}>*</span>
);

// Dezimalkomma statt -punkt; ganze Werte ohne Nachkommastelle ("4 MB", nicht "4,0 MB").
type FieldErrors = { name?: string; email?: string; message?: string };

const Kontakt: React.FC = () => {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [mail, setMail] = useState('');
  const [msg, setMsg] = useState('');
  const [topic, setTopic] = useState(TOPICS[0].value);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);

  // Honeypot: fuer Menschen unsichtbar, Bots fuellen ihn aus. Wird mitgeschickt und im Backend
  // bewertet — abgewiesen wird nichts, siehe InquirySpamCheck.
  const [website, setWebsite] = useState('');
  // Als Dauer gemessen, nicht als Zeitpunkt: so ist die Uhr des Besuchers egal.
  const mountedAt = useRef(Date.now());

  const nameRef = useRef<HTMLInputElement>(null);
  const mailRef = useRef<HTMLInputElement>(null);
  const msgRef = useRef<HTMLTextAreaElement>(null);

  /** Prueft alle Pflichtfelder und liefert die Meldungen — eine Quelle fuer Blur und Absenden. */
  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = 'Bitte trag deinen Namen ein.';
    if (!mail.trim()) next.email = 'Bitte trag deine E-Mail-Adresse ein.';
    else if (!EMAIL_REGEX.test(mail.trim())) {
      next.email = 'Diese Adresse sieht nicht vollständig aus — es fehlt etwas wie „@anbieter.de".';
    }
    if (!msg.trim()) next.message = 'Bitte schreib uns, worum es geht.';
    return next;
  };

  /** Was noch fehlt, in einem Satz — steht unter dem Button, bevor jemand ihn drueckt. */
  const missingHint: string | null = (() => {
    const missing: string[] = [];
    if (!name.trim()) missing.push('dein Name');
    if (!mail.trim()) missing.push('deine E-Mail-Adresse');
    if (!msg.trim()) missing.push('deine Nachricht');
    if (missing.length === 0) {
      return EMAIL_REGEX.test(mail.trim()) ? null : 'Die E-Mail-Adresse ist noch unvollständig.';
    }
    const last = missing.pop();
    return `Es fehlt noch ${missing.length ? `${missing.join(', ')} und ${last}` : last}.`;
  })();

  const addFiles = (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    const incoming = Array.from(picked);
    const problems: string[] = [];
    const accepted: File[] = [];

    for (const f of incoming) {
      if (files.length + accepted.length >= MAX_ATTACHMENTS) {
        problems.push(`Mehr als ${MAX_ATTACHMENTS} Dateien gehen nicht — „${f.name}" wurde nicht übernommen.`);
        continue;
      }
      if (f.size > MAX_ATTACHMENT_BYTES) {
        problems.push(`„${f.name}" ist ${formatBytes(f.size)} groß, erlaubt sind ${formatBytes(MAX_ATTACHMENT_BYTES)}.`);
        continue;
      }
      if (files.some((e) => e.name === f.name && e.size === f.size)) continue;
      accepted.push(f);
    }

    if (accepted.length) setFiles((list) => [...list, ...accepted]);
    setFileError(problems.length ? problems.join(' ') : null);
  };

  const removeFile = (idx: number) => {
    setFiles((list) => list.filter((_, i) => i !== idx));
    setFileError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      // Die erste beanstandete Stelle anspringen, damit auf dem Phone klar ist, wo es klemmt.
      setError('Bitte ergänze noch die markierten Felder.');
      (found.name ? nameRef : found.email ? mailRef : msgRef).current?.focus();
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const inquiryId = await submitContactInquiry({
        name: name.trim(),
        company: company.trim() || undefined,
        email: mail.trim(),
        topic,
        message: msg.trim(),
        website,
        elapsedMs: Date.now() - mountedAt.current,
      });

      // Anhaenge sind Beiwerk: die Nachricht ist bereits sicher angekommen. Schlaegt eine Datei
      // fehl, wird das benannt statt die ganze Einreichung als gescheitert darzustellen.
      const failed: string[] = [];
      for (const f of files) {
        try {
          await uploadInquiryAttachment(inquiryId, f);
        } catch {
          failed.push(f.name);
        }
      }
      if (failed.length) {
        setUploadWarning(
          failed.length === 1
            ? `Nur „${failed[0]}" konnte nicht übertragen werden — schick die Datei gerne als Antwort auf unsere Mail.`
            : `${failed.length} Dateien konnten nicht übertragen werden — schick sie gerne als Antwort auf unsere Mail.`
        );
      }

      umamiTrack('kontakt-anfrage', { thema: topic, anhaenge: files.length });
      setSent(true);
    } catch {
      setError('Das hat leider nicht geklappt. Bitte versuch es in einem Moment noch einmal.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (invalid?: boolean): React.CSSProperties => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 14px',
    borderRadius: 10,
    border: `1px solid ${invalid ? '#C0392B' : '#E5E1E1'}`,
    background: '#fff',
    fontFamily: FONT_BODY,
    fontSize: 15,
    color: '#2C2C2C',
    outline: 'none',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: '0.1em',
    color: '#6A6A6A',
    margin: '0 0 6px',
    textTransform: 'uppercase',
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #E5E1E1',
    borderRadius: 18,
    padding: 28,
  };

  /** Fehlermeldung unter einem Feld — mit role="alert", damit Screenreader sie ansagen. */
  const FieldError: React.FC<{ text?: string }> = ({ text }) =>
    text ? (
      <div role="alert" style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#C0392B', marginTop: 6 }}>
        {text}
      </div>
    ) : null;

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
      </Helmet>

      <style>{`
        @keyframes kRise { from { opacity: 0; transform: translateY(26px); } to { opacity: 1; transform: translateY(0); } }
        .k-rise { animation: kRise 0.6s ease both; }

        @media (prefers-reduced-motion: reduce) {
          .k-rise { animation: none !important; opacity: 1 !important; transform: none !important; }
        }

        .k-input:focus { border-color: #922C57 !important; box-shadow: 0 0 0 3px rgba(146,44,87,0.12); }
        .k-input[aria-invalid="true"]:focus { border-color: #C0392B !important; box-shadow: 0 0 0 3px rgba(192,57,43,0.12); }

        .k-dropzone:hover { border-color: #922C57 !important; background: rgba(146,44,87,0.03) !important; }

        /* Honeypot: aus dem Bild geschoben statt display:none — Bots ueberspringen versteckte
           Felder teils gezielt, ein ausgelagertes Feld fuellen sie dagegen brav aus. */
        .k-hp { position: absolute; left: -9999px; top: 0; width: 1px; height: 1px; overflow: hidden; }

        @media (max-width: 860px) {
          .k-hero { padding: 40px 20px 44px !important; }
          .k-hero-h1 { font-size: 34px !important; }
          .k-container { padding: 28px 20px 48px !important; }
          .k-row { flex-direction: column !important; gap: 14px !important; }
        }
      `}</style>

      <div className="hide-mobile">
        <PublicHeader />
      </div>

      <StaticPageTopbar label="// KONTAKT" />

      {/* HERO-BAND */}
      <section
        className="k-hero"
        style={{
          background: 'linear-gradient(105deg,#3A1523 0%,#6E2440 52%,#A8355F 100%)',
          color: '#F5F3F3', padding: '48px 40px 54px',
        }}
      >
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.18em', color: '#F0C4D4' }}>{'// KONTAKT'}</span>
          <h1
            className="k-hero-h1"
            style={{
              fontFamily: FONT_DISPLAY, fontWeight: 900, fontSize: 46, lineHeight: 1.03,
              letterSpacing: '-0.025em', margin: '12px 0 0', maxWidth: 760, textWrap: 'pretty',
            }}
          >
            Schreibt uns
          </h1>
          <p style={{
            fontFamily: FONT_BODY, fontSize: 18, lineHeight: 1.55, color: '#E9D6DE',
            margin: '16px 0 0', maxWidth: 640, textWrap: 'pretty',
          }}>
            Fragen, Kritik oder Vorschläge? Ein Formular für alle Themen — und eine Antwort von
            der Person, die sich damit auskennt.
          </p>
        </div>
      </section>

      <div className="k-container" style={{ flex: 1, maxWidth: 1180, width: '100%', boxSizing: 'border-box', margin: '0 auto', padding: '44px 40px 60px' }}>

        {/* FORMULAR */}
        <section className="k-rise" style={{ ...cardStyle, marginBottom: 34 }} aria-labelledby="k-form-title">
          {sent ? (
            <div style={{ textAlign: 'center', padding: '28px 8px' }}>
              <div style={{
                width: 54, height: 54, borderRadius: '50%', background: 'rgba(146,44,87,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                fontSize: 26, color: '#922C57',
              }}>
                ✓
              </div>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, color: '#2C2C2C', margin: '0 0 8px' }}>
                Nachricht ist angekommen
              </h2>
              <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.6, color: '#6A6A6A', margin: 0, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
                Danke! Wir haben eure Anfrage im Posteingang und melden uns an
                <strong style={{ color: '#2C2C2C' }}> {mail.trim()}</strong>.
              </p>
              {uploadWarning && (
                <p role="alert" style={{
                  fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.6, color: '#8A6112',
                  background: 'rgba(191,132,26,0.10)', border: '1px solid rgba(191,132,26,0.28)',
                  borderRadius: 10, padding: '10px 14px', margin: '18px auto 0', maxWidth: 460,
                }}>
                  {uploadWarning}
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h2 id="k-form-title" style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, color: '#2C2C2C', margin: '0 0 6px' }}>
                Kontaktformular
              </h2>
              <p style={{ fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.55, color: '#6A6A6A', margin: '0 0 22px' }}>
                Wählt ein Thema, damit eure Nachricht direkt bei der richtigen Person landet.
              </p>

              {/* Thema */}
              <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px' }}>
                <legend style={{ ...labelStyle, padding: 0 }}>Thema</legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {TOPICS.map((t) => {
                    const on = topic === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setTopic(t.value)}
                        aria-pressed={on}
                        title={t.hint}
                        style={{
                          fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600,
                          padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                          border: `1px solid ${on ? '#922C57' : '#E5E1E1'}`,
                          background: on ? 'rgba(146,44,87,0.10)' : '#fff',
                          color: on ? '#922C57' : '#6A6A6A',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {t.value}
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#9A9494', margin: '10px 0 0' }}>
                  {TOPICS.find((t) => t.value === topic)?.hint}
                </p>
              </fieldset>

              <div className="k-row" style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle} htmlFor="k-name">Name<RequiredMark /></label>
                  <input
                    id="k-name" ref={nameRef} className="k-input" style={inputStyle(!!errors.name)} value={name}
                    onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: undefined })); }}
                    onBlur={() => setErrors((p) => ({ ...p, name: validate().name }))}
                    aria-invalid={!!errors.name} aria-describedby={errors.name ? 'k-name-err' : undefined}
                    autoComplete="name"
                  />
                  <span id="k-name-err"><FieldError text={errors.name} /></span>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle} htmlFor="k-mail">E-Mail<RequiredMark /></label>
                  <input
                    id="k-mail" ref={mailRef} className="k-input" style={inputStyle(!!errors.email)} value={mail} type="email"
                    onChange={(e) => { setMail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
                    onBlur={() => setErrors((p) => ({ ...p, email: validate().email }))}
                    aria-invalid={!!errors.email} aria-describedby={errors.email ? 'k-mail-err' : undefined}
                    autoComplete="email"
                  />
                  <span id="k-mail-err"><FieldError text={errors.email} /></span>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle} htmlFor="k-company">Verein oder Firma <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  id="k-company" className="k-input" style={inputStyle()} value={company}
                  onChange={(e) => setCompany(e.target.value)} autoComplete="organization"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle} htmlFor="k-msg">Nachricht<RequiredMark /></label>
                <textarea
                  id="k-msg" ref={msgRef} className="k-input"
                  style={{ ...inputStyle(!!errors.message), minHeight: 140, resize: 'vertical' }}
                  value={msg}
                  onChange={(e) => { setMsg(e.target.value); if (errors.message) setErrors((p) => ({ ...p, message: undefined })); }}
                  onBlur={() => setErrors((p) => ({ ...p, message: validate().message }))}
                  aria-invalid={!!errors.message} aria-describedby={errors.message ? 'k-msg-err' : undefined}
                  maxLength={4000}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
                  <span id="k-msg-err"><FieldError text={errors.message} /></span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: '#9A9494', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                    {msg.length} / 4000
                  </span>
                </div>
              </div>

              {/* Anhaenge */}
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle} htmlFor="k-files">
                  Dateien <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                </label>
                <label
                  className="k-dropzone"
                  htmlFor="k-files"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                    border: '1px dashed #D6D0D0', borderRadius: 12, padding: '14px 16px',
                    background: '#FBFAFA', transition: 'border-color 0.15s ease, background 0.15s ease',
                  }}
                >
                  <span aria-hidden style={{ fontSize: 20, color: '#922C57' }}>＋</span>
                  <span>
                    <span style={{ display: 'block', fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: '#2C2C2C' }}>
                      Screenshot oder Foto anhängen
                    </span>
                    <span style={{ display: 'block', fontFamily: FONT_BODY, fontSize: 13, color: '#9A9494', marginTop: 2 }}>
                      Bis zu {MAX_ATTACHMENTS} Dateien, je max. {formatBytes(MAX_ATTACHMENT_BYTES)} · JPEG, PNG, WebP oder PDF
                    </span>
                  </span>
                </label>
                <input
                  id="k-files" type="file" multiple accept={ACCEPTED_ATTACHMENTS}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                  style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                />

                {files.length > 0 && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {files.map((f, i) => (
                      <li key={`${f.name}-${f.size}`} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        border: '1px solid #E5E1E1', borderRadius: 10, padding: '9px 12px', background: '#fff',
                      }}>
                        <span style={{ flex: 1, minWidth: 0, fontFamily: FONT_BODY, fontSize: 14, color: '#2C2C2C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.name}
                        </span>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: '#9A9494', whiteSpace: 'nowrap' }}>
                          {formatBytes(f.size)}
                        </span>
                        <button
                          type="button" onClick={() => removeFile(i)}
                          aria-label={`${f.name} entfernen`}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#922C57', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {fileError && (
                  <div role="alert" style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#C0392B', marginTop: 8 }}>
                    {fileError}
                  </div>
                )}
              </div>

              {/* Honeypot — nicht ausfuellen; fuer Menschen unsichtbar. */}
              <div className="k-hp" aria-hidden="true">
                <label htmlFor="k-website">Website (bitte frei lassen)</label>
                <input
                  id="k-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                  value={website} onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              {error && (
                <div role="alert" style={{
                  fontFamily: FONT_BODY, fontSize: 14, color: '#922C57', background: 'rgba(146,44,87,0.08)',
                  border: '1px solid rgba(146,44,87,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700,
                    padding: '12px 26px', borderRadius: 999, border: 'none',
                    background: submitting ? '#D9D3D3' : '#922C57',
                    color: '#fff', cursor: submitting ? 'progress' : 'pointer',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {submitting ? 'Wird gesendet…' : 'Nachricht senden'}
                </button>
                <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#9A9494' }}>
                  <RequiredMark /> Pflichtfeld
                </span>
              </div>

              {/* Sagt schon vor dem Klick, was noch fehlt — gleiche Idee wie der Hinweis unter dem
                  Button auf der "Für Partner"-Seite. Der Button bleibt hier trotzdem klickbar:
                  bei diesem laengeren Formular muss die Meldung an das betroffene Feld zeigen. */}
              {!submitting && missingHint && (
                <p style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', margin: '10px 0 0' }}>
                  {missingHint}
                </p>
              )}
            </form>
          )}
        </section>

        {/* PRESSE & DOWNLOADS */}
        <section className="k-rise" style={{ animationDelay: '0.08s' }} aria-labelledby="k-presse-title">
          <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.18em', color: '#922C57' }}>{'// PRESSE'}</span>
          <h2 id="k-presse-title" style={{
            fontFamily: FONT_DISPLAY, fontWeight: 900, fontSize: 32, lineHeight: 1.1,
            letterSpacing: '-0.02em', color: '#2C2C2C', margin: '10px 0 24px',
          }}>
            Pressemitteilungen
          </h2>

          <div style={cardStyle}>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, color: '#2C2C2C', margin: '0 0 12px' }}>
              Veröffentlichung von Pressemitteilungen
            </h3>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.65, color: '#4A4A4A', margin: '0 0 14px' }}>
              Du möchtest deine Radsportveranstaltung vorstellen und überregional bekannt machen?
              Kein Problem. Schick uns gerne einen Pressetext — am besten inklusive Logo
              beziehungsweise Fotos —, der dann in einem Newsbeitrag auf unserer Seite
              veröffentlicht wird.
            </p>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.65, color: '#4A4A4A', margin: '0 0 14px' }}>
              Bitte gib bei eingesandten Fotos den Fotocredit an und stelle sicher, dass der
              Fotograf die Erlaubnis zur honorarfreien Veröffentlichung auf unserer Seite
              erteilt hat.
            </p>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.65, color: '#4A4A4A', margin: '0 0 18px' }}>
              Bitte beachte außerdem: Ein Anspruch auf Veröffentlichung besteht nicht, und die
              Redaktion behält sich vor, Beiträge abzuändern oder zu kürzen.
            </p>
            <div style={{
              fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.6, color: '#6A6A6A',
              background: 'rgba(146,44,87,0.06)', border: '1px solid rgba(146,44,87,0.18)',
              borderRadius: 12, padding: '12px 16px',
            }}>
              Schick uns deinen Pressetext über das Formular oben mit dem Thema
              <strong style={{ color: '#922C57' }}> „Presse &amp; Medien"</strong> — Fotos und den
              Text als PDF kannst du direkt anhängen.
            </div>
          </div>
        </section>
      </div>

    </div>
  );
};

export default Kontakt;
